// TODO: https://github.com/combinaut/sparkle/issues/31058 rewrites this into SpFinder and deletes legacy/
import { connectLegacyFinder } from "./legacy-adapter";
import { createEventsSearch } from "#ce/apps/SpFinder/store/events-search";
import { createEventsRenderer } from "./events-results";
import { MOUNT_READY_EVENT, whenMountReady } from "#ce/framework/mount-ready";
import { distanceFilterNames } from "#ce/shared/filters/distance/filter-names";

const transports = new WeakMap();

// The form controls that submit no value until they have mounted.
const CUSTOM_ELEMENT_CONTROLS = "sp-finder-omnisearch, sp-finder-filter";

// Returns the params a control submits. A distance filter submits its location and coordinates beside
// its `name`.
function submittedNames(control) {
  if (control.localName === "sp-finder-filter" && control.getAttribute("type") === "distance") {
    return distanceFilterNames(control.getAttribute("location-param")?.trim() || undefined);
  }

  return [control.getAttribute("name")];
}

/** Connects an events finder to delegated form/link commands and returns its teardown handle. */
export function createEventsTransport(container, options = {}) {
  const root = typeof container === "string" ? document.querySelector(container) : container;
  const host = root?.matches('sp-finder[kind="events"]') ? root : root?.querySelector('sp-finder[kind="events"]');
  if (!host) {
    throw new Error("Events search requires an events finder wrapper");
  }
  transports.get(host)?.disconnect();
  const render = createEventsRenderer(host);
  let writer;
  let service;
  let closed = false;
  let pendingSearch;

  function attach() {
    if (closed || writer) {
      return;
    }
    writer = connectLegacyFinder(host);
    service = createEventsSearch({
      ...options,
      publish: publishSearch,
      reportRequest: writer.reportRequest,
      reportCompletion: writer.reportCompletion,
      reportError: writer.reportError,
      render,
    });
    writer.setActions({ search: service.search, dispose: suspend });
    service.initialize();
    host.addEventListener("submit", handleSubmit);
    host.addEventListener("change", handleChange);
    host.addEventListener("click", handleLink);
    if (pendingSearch) {
      const command = pendingSearch;
      pendingSearch = undefined;
      writer.search(...command);
    }
  }

  function resume(event) {
    if (event.target === host && host.isConnected) {
      attach();
    }
  }

  function publishSearch(params) {
    writer.publishSearch(params, { filterValues: params });
  }

  function getResults(value, commandOptions) {
    if (closed) {
      return;
    }
    if (writer) {
      writer.search(value, commandOptions);
    } else {
      pendingSearch = [value, commandOptions];
    }
  }

  // Searches the form's values once its custom-element controls have settled. It cancels older work before
  // waiting, and a search asked for while it waits, such as a followed link or a history navigation,
  // supersedes it.
  async function submitForm(form, commandOptions) {
    const claimedService = service;
    const claimed = claimedService?.claim();
    const params = await formParams(form);
    if (closed || (claimedService && (service !== claimedService || service.searchCount !== claimed))) {
      return;
    }
    const url = new URL(form.action, window.location.href);
    for (const name of new Set(params.keys())) {
      url.searchParams.delete(name);
    }
    for (const [name, value] of params) {
      if (value !== "") {
        url.searchParams.append(name, value);
      }
    }
    getResults(url.toString(), commandOptions);
  }

  // Returns the form's values once each custom-element control has mounted or failed to load. A control
  // that failed to load takes the address bar's values for the params it submits, except a param the
  // form already submits.
  async function formParams(form) {
    const controls = [...form.querySelectorAll(CUSTOM_ELEMENT_CONTROLS)];
    const settlements = await Promise.allSettled(controls.map(whenMountReady));
    const params = new URLSearchParams(new FormData(form));
    const current = service?.currentUrl().searchParams;

    controls.forEach((control, index) => {
      if (settlements[index].status !== "rejected") {
        return;
      }

      submittedNames(control).forEach((name) => {
        if (name && !params.has(name)) {
          current?.getAll(name).forEach((value) => params.append(name, value));
        }
      });
    });

    return params;
  }

  function handleSubmit(event) {
    if (!event.target.matches("form")) {
      return;
    }
    event.preventDefault();
    submitForm(event.target);
  }

  function handleChange(event) {
    if (event.target.closest?.("sp-finder-omnisearch")) {
      return;
    }
    const form = event.target.closest?.('form[data-behaviour~="submit-on-change"]');
    if (form) {
      submitForm(form, { refreshUI: false });
    }
  }

  function handleLink(event) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    const link = event.target.closest?.(
      '.pagination [href], .collection-pagination [href], .results-control [href], [data-behaviour~="embedded-index-link"]',
    );
    const target = link?.getAttribute("target");
    if (!link || link.hasAttribute("download") || (target && target !== "_self")) {
      return;
    }
    event.preventDefault();
    const href = new URL(link.getAttribute("href"), link.baseURI).toString();
    getResults(options.undecorateUrl ? options.undecorateUrl(href) : href, { scrollIntoView: true, refreshUI: false });
  }

  function suspend() {
    host.removeEventListener("submit", handleSubmit);
    host.removeEventListener("change", handleChange);
    host.removeEventListener("click", handleLink);
    service?.dispose();
    writer?.disconnect();
    service = undefined;
    writer = undefined;
  }

  function disconnect() {
    closed = true;
    pendingSearch = undefined;
    suspend();
    host.removeEventListener(MOUNT_READY_EVENT, resume);
    if (transports.get(host) === transport) {
      transports.delete(host);
    }
  }
  const transport = Object.freeze({ getResults, submitForm, render, disconnect });
  transports.set(host, transport);
  host.addEventListener(MOUNT_READY_EVENT, resume);
  attach();
  return transport;
}
